import React from "react";
import {FileSearchOutlined} from "@ant-design/icons";
import {Text, TextAs, TextSizes} from "@clockout-dev/design-elements";
import SectionCard from "../../common/sectionCard/SectionCard";
import ConfidenceMeter from "./ConfidenceMeter";
import {EXPECTED_DOC_TYPE_BY_MODE} from "../uploads-simulator.constants";
import pillStyles from "../styles/pills.module.scss";
import styles from "../styles/results.module.scss";

/**
 * Per-document classification: confirmed/denied pill, actual doc type vs the
 * mode's expected type, the confidence meter, verification method/latency and
 * PDF provenance metadata when the report carries it.
 *
 * @param {Object} props
 * @param {Object} [props.verdict] — entry from report `verdicts`
 * @param {string} [props.mode]
 */
function ClassificationCard({verdict, mode}) {
    const confirmed = verdict?.doc_type_confirmed === true;
    const metadata = verdict?.document_metadata && typeof verdict.document_metadata === "object" ? Object.entries(verdict.document_metadata) : [];

    return (
        <SectionCard icon={<FileSearchOutlined />} title="Classification & Confidence">
            <div className={styles.kvGrid}>
                <div className={styles.kvRow}>
                    <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.kvLabel}>
                        Type confirmed
                    </Text>
                    <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={`${pillStyles.tag} ${confirmed ? pillStyles.tone_success : pillStyles.tone_danger}`}>
                        {verdict ? (confirmed ? "Confirmed" : "Not confirmed") : "—"}
                    </Text>
                </div>
                <div className={styles.kvRow}>
                    <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.kvLabel}>
                        Detected type
                    </Text>
                    <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.kvValue}>
                        {verdict?.doc_type_actual || "—"}
                    </Text>
                </div>
                <div className={styles.kvRow}>
                    <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.kvLabel}>
                        Expected for mode
                    </Text>
                    <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.kvValue}>
                        {EXPECTED_DOC_TYPE_BY_MODE[mode] || "—"}
                    </Text>
                </div>
                <div className={styles.kvRow}>
                    <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.kvLabel}>
                        Confidence
                    </Text>
                    <ConfidenceMeter confidence={verdict?.confidence} />
                </div>
                <div className={styles.kvRow}>
                    <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.kvLabel}>
                        Method / latency
                    </Text>
                    <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.kvValue}>
                        {verdict?.method || "—"}
                        {typeof verdict?.latency_ms === "number" ? ` · ${(verdict.latency_ms / 1000).toFixed(1)}s` : ""}
                    </Text>
                </div>
                {verdict?.reason ? (
                    <div className={styles.kvRow}>
                        <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.kvLabel}>
                            Model summary
                        </Text>
                        {/* `verdict.reason` is the model's free-text summary — present on
                            accepted documents too, so it only styles as danger when the
                            doc-type gate actually failed. Engine ruling codes live in
                            `result.reason` (RulingBanner). */}
                        <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={verdict?.doc_type_confirmed === false ? styles.kvValueDanger : styles.kvValue}>
                            {verdict.reason}
                        </Text>
                    </div>
                ) : null}
                {verdict?.error ? (
                    <div className={styles.kvRow}>
                        <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.kvLabel}>
                            AI error
                        </Text>
                        <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.kvValueDanger}>
                            {String(verdict.error)}
                        </Text>
                    </div>
                ) : null}
            </div>

            {metadata.length ? (
                <div className={styles.metadataBlock}>
                    <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.metadataTitle}>
                        Document metadata (PDF provenance)
                    </Text>
                    {metadata.map(([key, value]) => (
                        <Text key={key} as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.metadataRow}>
                            <span className={styles.metadataKey}>{key}:</span> {typeof value === "object" ? JSON.stringify(value) : String(value)}
                        </Text>
                    ))}
                </div>
            ) : null}
        </SectionCard>
    );
}

export default ClassificationCard;
